// ─── Fantasy Football domain types ───────────────────────────

export type FantasyProvider = 'yahoo' | 'espn'
export type Position = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DST'
export type ScoringFormat = 'standard' | 'half-ppr' | 'ppr'
export type InjuryRisk = 'low' | 'medium' | 'high'

export const POSITIONS: Position[] = ['QB', 'RB', 'WR', 'TE', 'K', 'DST']

// ─── League connections (Yahoo / ESPN OAuth) ─────────────────

export interface LeagueConnection {
  id: string
  provider: FantasyProvider
  status: 'connected' | 'expired' | 'error'
  leagueName: string
  leagueId: string
  teamName: string
  teamCount: number
  scoring: ScoringFormat
  lastSync: string | null
}

// ─── Players & projections ───────────────────────────────────

export interface ProjectionSource {
  /** 'yahoo' | 'espn' | 'consensus' (aggregate expert site) */
  source: string
  /** Projected season fantasy points from this source */
  points: number
}

export interface Player {
  id: string
  name: string
  position: Position
  nflTeam: string
  byeWeek: number
  /** Average draft position across public drafts (lower = earlier) */
  adp: number
  projections: ProjectionSource[]
  /** Historical week-to-week scoring standard deviation (points/week) */
  weeklyStdDev: number
  injuryRisk: InjuryRisk
  rosterStatus: 'available' | 'mine' | 'taken'
  ownedByTeam?: string
}

// ─── Analytics output ────────────────────────────────────────

export interface PlayerAnalytics {
  playerId: string
  /** F1: source-weighted consensus season points */
  consensusPoints: number
  /** F2: season projection uncertainty (points) */
  projectionStdDev: number
  /** F3+F4: consensus after injury availability and schedule strength */
  adjustedPoints: number
  /** F5: replacement-level season points for the position */
  replacementPoints: number
  /** F5: value over replacement */
  vorp: number
  /** F6: marginal covariance vs current roster (weekly points², >0 = adds correlated risk) */
  rosterCovariance: number
  /** F7: bye-week overlap penalty (season points equivalent) */
  byeOverlapPenalty: number
  /** F8: mean–variance certainty equivalent value */
  riskAdjustedValue: number
  /** F9: after user positional scarcity weights */
  scarcityWeightedValue: number
  /** F11: change in expected season wins if acquired for the current roster */
  expectedWinsDelta: number
  /** Human-readable drivers, e.g. "Shares Week 9 bye with 2 starters" */
  notes: string[]
}

export interface RankedPlayer {
  player: Player
  analytics: PlayerAnalytics
  rank: number
}

// ─── Roster & season outlook ─────────────────────────────────

export interface RosterRequirements {
  QB: number
  RB: number
  WR: number
  TE: number
  FLEX: number
  K: number
  DST: number
  bench: number
}

export interface WeekOutlook {
  week: number
  opponent: string
  projectedFor: number
  projectedAgainst: number
  rosterStdDev: number
  /** F10: P(win) under the Normal matchup model */
  winProbability: number
  /** Starters on bye this week */
  byeOuts: string[]
}

export interface SeasonOutlook {
  /** F11: sum of weekly win probabilities */
  expectedWins: number
  totalWeeks: number
  weeks: WeekOutlook[]
}

// ─── Risk preferences (user-tunable) ─────────────────────────

export interface RiskPreferences {
  /** λ in F8 — how many points of value one unit of variance costs */
  riskAversion: number
  /** F9 — per-position scarcity multipliers (1 = neutral, >1 = user expects scarcity) */
  positionScarcity: Record<Position, number>
  /** F7 — weight on same-bye overlap penalties */
  byePenaltyWeight: number
  /** F6 — scales all teammate correlation effects */
  teammateCorrelationWeight: number
  /** θ in F4 — how strongly schedule strength moves projections */
  scheduleWeight: number
  /** F1 — relative trust in each projection source */
  sourceWeights: Record<string, number>
}

// ─── Draft board ─────────────────────────────────────────────

export interface DraftSlot {
  overallRank: number
  playerId: string
  /** F12 score at the time the board was generated */
  score: number
  reason: string
}

export interface DraftBoard {
  id: string
  generatedAt: string
  approvedAt: string | null
  status: 'proposed' | 'approved'
  slots: DraftSlot[]
}

// ─── Acquisition recommendations ─────────────────────────────

export interface AcquisitionRecommendation {
  playerId: string
  playerName: string
  position: Position
  /** Roster player to drop, if the roster is full */
  dropPlayerId: string | null
  dropPlayerName: string | null
  deltaExpectedWins: number
  deltaRiskAdjustedValue: number
  reason: string
}

// ─── Knowledge base ──────────────────────────────────────────

export type FormulaCategory =
  | 'projection'
  | 'risk'
  | 'schedule'
  | 'valuation'
  | 'draft'
  | 'acquisition'

export interface FormulaParameter {
  key: string
  label: string
  value: number
  defaultValue: number
  min: number
  max: number
  step: number
  description: string
}

export interface Formula {
  id: string
  name: string
  category: FormulaCategory
  /** Plain-text math, e.g. "μᵢ = Σₛ wₛ·pᵢ,ₛ / Σₛ wₛ" */
  formula: string
  inputs: string[]
  rationale: string
  usedIn: string[]
  parameters: FormulaParameter[]
}

export interface AuditRecord {
  id: string
  timestamp: string
  action:
    | 'draft-order-generated'
    | 'draft-order-approved'
    | 'acquisition-recommended'
    | 'player-acquired'
    | 'parameters-changed'
    | 'preferences-changed'
    | 'league-synced'
  detail: string
  formulaIds: string[]
  /** Snapshot of every tunable parameter at decision time */
  parameterSnapshot: Record<string, number>
}

// ─── Summary (Overview tab) ──────────────────────────────────

export interface FantasySummary {
  connectedLeagues: number
  rosterSize: number
  expectedWins: number
  totalWeeks: number
  draftBoardStatus: 'none' | 'proposed' | 'approved'
  topAvailable: { playerId: string; name: string; position: Position; value: number }[]
  lastSync: string | null
}
